import React, { useLayoutEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './LogoAnimated.module.css';
import { A, L, N, ORB_FILL, ORB_STROKE_CIRCLE, U } from './logoPaths';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LogoAnimatedProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width in px. Height scales proportionally via SVG aspect ratio. Default: 200 */
  size?: number;
  /** Whether the animation loops indefinitely. Default: true */
  loop?: boolean;
  /** Total animation duration in ms. Default: 3340 */
  duration?: number;
  /** Hold time at completion before restarting (ms). Default: 1000 */
  holdDuration?: number;
}

// ── SVG path data ─────────────────────────────────────────────────────────────
// Path data shared with EmblemAnimated — see ./logoPaths.ts.

// Shape clip paths (inner stroke) — same paths used for clip-path on stroke layer
const SHAPE_CLIPS = { orb: ORB_FILL, n: N, u: U, l: L, a: A };

// Fill clip element x-bounds for letter rects
const LETTER_BOUNDS = {
  n: { xMin: 924, xMax: 1002 },
  u: { xMin: 1008, xMax: 1092 },
  l: { xMin: 1098, xMax: 1134 },
  a: { xMin: 1126, xMax: 1216 },
} as const;

type ShapeKey = 'orb' | 'n' | 'u' | 'l' | 'a';

// ── Component ─────────────────────────────────────────────────────────────────

export function LogoAnimated({
  size = 200,
  loop = true,
  duration = 3340,
  holdDuration = 1000,
  className,
  style,
  ...props
}: LogoAnimatedProps) {
  const strokeRefs = useRef<Partial<Record<ShapeKey, SVGPathElement | null>>>({});
  const fillOrbRef = useRef<SVGCircleElement | null>(null);
  const fillRectRefs = useRef<Partial<Record<Exclude<ShapeKey, 'orb'>, SVGRectElement | null>>>({});
  const strokeSvgRef = useRef<SVGSVGElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    // Measure all stroke path lengths, assign sequential start times
    const shapes: ShapeKey[] = ['orb', 'n', 'u', 'l', 'a'];
    const segs = shapes.map(key => {
      const el = strokeRefs.current[key];
      const len = el && typeof el.getTotalLength === 'function' ? el.getTotalLength() : 0;
      return { key, el, len };
    });

    const totalLen = segs.reduce((s, seg) => s + seg.len, 0);

    let cursor = 0;
    const timed = segs.map(seg => {
      const start = cursor;
      const dur = totalLen > 0 ? (seg.len / totalLen) * duration : 0;
      cursor += dur;
      return { ...seg, start, dur };
    });

    // Init dasharray
    timed.forEach(({ el, len }) => {
      if (!el) return;
      el.style.strokeDasharray = `${len}px`;
      el.style.strokeDashoffset = `${len}px`;
    });

    function reset() {
      timed.forEach(({ el, len }) => {
        if (el) el.style.strokeDashoffset = `${len}px`;
      });
      if (fillOrbRef.current) fillOrbRef.current.setAttribute('r', '0');
      (['n', 'u', 'l', 'a'] as const).forEach(k => {
        fillRectRefs.current[k]?.setAttribute('width', '0');
      });
      if (strokeSvgRef.current) strokeSvgRef.current.style.opacity = '1';
    }

    function runCycle(cycleStart: number) {
      reset();

      function tick(now: number) {
        const t = now - cycleStart;

        timed.forEach(({ key, el, len, start, dur }) => {
          if (!el || t < start) return;
          const raw = Math.min((t - start) / dur, 1);
          el.style.strokeDashoffset = `${len * (1 - raw)}px`;

          if (key === 'orb') {
            fillOrbRef.current?.setAttribute('r', String(92 * raw));
          } else {
            const bounds = LETTER_BOUNDS[key as Exclude<ShapeKey, 'orb'>];
            fillRectRefs.current[key as Exclude<ShapeKey, 'orb'>]?.setAttribute(
              'width',
              String((bounds.xMax - bounds.xMin) * raw),
            );
          }
        });

        if (t > duration && strokeSvgRef.current) {
          strokeSvgRef.current.style.opacity = String(
            Math.max(0, 1 - (t - duration) / 150),
          );
        }

        if (t < duration + holdDuration) {
          rafRef.current = requestAnimationFrame(tick);
        } else if (loop) {
          rafRef.current = requestAnimationFrame(ts => runCycle(ts));
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(ts => runCycle(ts));

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [duration, holdDuration, loop]);

  const setStrokeRef = (key: ShapeKey) => (el: SVGPathElement | null) => {
    strokeRefs.current[key] = el;
  };

  const setFillRectRef = (key: Exclude<ShapeKey, 'orb'>) => (el: SVGRectElement | null) => {
    fillRectRefs.current[key] = el;
  };

  // Width is driven by the `--logo-width` CSS var on the root. Setting it here as
  // a pure-CSS-var style object is allowed by no-restricted-syntax. User-supplied
  // `style` is merged into the same object.
  const rootStyle = { '--logo-width': `${size}px`, ...style } as React.CSSProperties;

  return (
    <div
      role="img"
      aria-label="Nula logo"
      className={clsx(styles.root, className)}
      style={rootStyle}
      {...props}
    >
      {/* Fill layer */}
      <svg viewBox="700 430 580 220" xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Aurora gradient for the orb, mapped across its bounds (centre
              795.28,540 r≈87) bottom-right → top-left, matching the emblem art. */}
          <linearGradient
            id="la-orb-gradient"
            x1="858"
            y1="603"
            x2="732"
            y2="477"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#5ec8e5" />
            <stop offset="0.49" stopColor="#72d6c9" />
            <stop offset="1" stopColor="#a6e3f2" />
          </linearGradient>
          <clipPath id="la-clip-orb">
            <circle ref={fillOrbRef} cx="795.28" cy="540" r="0" />
          </clipPath>
          <clipPath id="la-clip-n">
            <rect ref={setFillRectRef('n')} x="924" y="425" width="0" height="230" />
          </clipPath>
          <clipPath id="la-clip-u">
            <rect ref={setFillRectRef('u')} x="1008" y="425" width="0" height="230" />
          </clipPath>
          <clipPath id="la-clip-l">
            <rect ref={setFillRectRef('l')} x="1098" y="425" width="0" height="230" />
          </clipPath>
          <clipPath id="la-clip-a">
            <rect ref={setFillRectRef('a')} x="1126" y="425" width="0" height="230" />
          </clipPath>
        </defs>
        <path clipPath="url(#la-clip-orb)" fill="url(#la-orb-gradient)" d={ORB_FILL} />
        <path clipPath="url(#la-clip-n)" className={styles.fillWord} d={N} />
        <path clipPath="url(#la-clip-u)" className={styles.fillWord} d={U} />
        <path clipPath="url(#la-clip-l)" className={styles.fillWord} d={L} />
        <path clipPath="url(#la-clip-a)" className={styles.fillWord} d={A} />
      </svg>

      {/* Stroke layer — clipped inside each shape so stroke only paints inward */}
      <svg
        ref={strokeSvgRef}
        className={styles.stroke}
        viewBox="700 430 580 220"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            id="la-stroke-gradient"
            x1="858"
            y1="603"
            x2="732"
            y2="477"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#5ec8e5" />
            <stop offset="0.49" stopColor="#72d6c9" />
            <stop offset="1" stopColor="#a6e3f2" />
          </linearGradient>
          {(Object.entries(SHAPE_CLIPS) as [ShapeKey, string][]).map(([key, d]) => (
            <clipPath key={key} id={`la-sc-${key}`}>
              <path d={d} />
            </clipPath>
          ))}
        </defs>
        <path
          ref={setStrokeRef('orb')}
          clipPath="url(#la-sc-orb)"
          className={styles.strokeShared}
          stroke="url(#la-stroke-gradient)"
          d={ORB_STROKE_CIRCLE}
        />
        {(['n', 'u', 'l', 'a'] as const).map(key => {
          const pathData = { n: N, u: U, l: L, a: A }[key];
          return (
            <path
              key={key}
              ref={setStrokeRef(key)}
              clipPath={`url(#la-sc-${key})`}
              className={clsx(styles.strokeShared, styles.strokeWord)}
              d={pathData}
            />
          );
        })}
      </svg>
    </div>
  );
}
